import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'

export type PromoSlot = 'beranda_slideshow' | 'beranda_banner' | 'katalog_banner' | 'dasbor_banner'
export type PromoKind = 'slide' | 'ad'
export type PromoAccent = 'hijau' | 'ungu' | 'kuning' | 'gelap'
export type PromoStatus = 'draft' | 'active' | 'paused'

export interface Promo {
  id: string
  kind: PromoKind
  slot: PromoSlot
  title: string
  subtitle?: string
  image_url?: string
  cta_label?: string
  target_url?: string
  accent: PromoAccent
  advertiser?: string
}

export interface AdminPromo extends Promo {
  price: number
  sort_order: number
  status: PromoStatus
  starts_at?: string
  ends_at?: string
  impressions: number
  clicks: number
  click_rate: number
  live: boolean
  created_at: string
}

export interface PromoInput {
  kind: PromoKind
  slot: PromoSlot
  title: string
  subtitle: string
  image_url: string
  cta_label: string
  target_url: string
  accent: PromoAccent
  advertiser: string
  price: number
  sort_order: number
  status: PromoStatus
  starts_at?: string
  ends_at?: string
}

/**
 * usePromos membaca tayangan yang sedang aktif pada sebuah slot.
 *
 * Kegagalannya sengaja tidak pernah ditampilkan sebagai galat di halaman.
 * Slideshow adalah hiasan etalase; beranda yang menolak tampil karena
 * banner-nya gagal dimuat jauh lebih merugikan daripada beranda tanpa banner.
 */
export function usePromos(slot: PromoSlot) {
  return useQuery({
    queryKey: ['promos', slot],
    queryFn: () => api.get<{ promos: Promo[] }>('/promos', { query: { slot } }),
    // Tayangan jarang berubah dan tidak menentukan apa pun yang penting;
    // memuatnya ulang setiap kali jendela difokuskan hanya menambah lalu
    // lintas tanpa menambah kebenaran.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

export function useAdminPromos() {
  return useQuery({
    queryKey: ['admin-promos'],
    queryFn: () => api.get<{ promos: AdminPromo[] }>('/admin/promos'),
  })
}

function usePromoMutation<TInput, TData>(mutationFn: (input: TInput) => Promise<TData>) {
  const client = useQueryClient()

  return useMutation({
    mutationFn,
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['admin-promos'] })
      client.invalidateQueries({ queryKey: ['promos'] })
    },
  })
}

export function useCreatePromo() {
  return usePromoMutation((input: PromoInput) => api.post<{ promo: AdminPromo }>('/admin/promos', input))
}

export function useUpdatePromo() {
  return usePromoMutation(({ id, ...input }: PromoInput & { id: string }) =>
    api.patch<{ promo: AdminPromo }>(`/admin/promos/${id}`, input),
  )
}

export function useDeletePromo() {
  return usePromoMutation((id: string) => api.delete<void>(`/admin/promos/${id}`))
}

/** useUploadPromoImage mengunggah gambar tayangan dan mengembalikan alamatnya. */
export function useUploadPromoImage() {
  return useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData()
      body.append('file', file)

      // Unggahan multipart tidak lewat klien JSON: badannya dibentuk peramban,
      // dan menyetel Content-Type sendiri justru menghapus batas multipart
      // yang ikut dihasilkannya.
      const response = await fetch('/api/v1/admin/promos/image', {
        method: 'POST',
        credentials: 'include',
        body,
      })

      const payload = await response.json()
      if (!response.ok) {
        throw new Error(payload?.error?.message ?? 'Gambar gagal diunggah.')
      }
      return payload as { image_url: string }
    },
  })
}

/**
 * lacakPromo mengirim hitungan tayang atau klik.
 *
 * Sengaja bukan hook mutation: hitungan ini tidak punya keadaan yang perlu
 * ditampilkan, tidak perlu dicoba ulang, dan kegagalannya tidak boleh
 * terlihat pengunjung. Yang dipakai adalah sendBeacon bila tersedia, supaya
 * klik yang langsung disusul perpindahan halaman tetap terkirim — fetch biasa
 * dibatalkan peramban saat halaman ditinggalkan.
 */
export function lacakPromo(id: string, event: 'view' | 'click'): void {
  const path = `/api/v1/promos/${id}/track`
  const body = JSON.stringify({ event })

  if (event === 'click' && typeof navigator.sendBeacon === 'function') {
    navigator.sendBeacon(path, new Blob([body], { type: 'application/json' }))
    return
  }

  void fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body,
    keepalive: true,
  }).catch(() => {
    // Hitungan yang hilang tidak berarti apa-apa bagi pengunjung.
  })
}

export interface PromoPackage {
  code: string
  name: string
  days: number
  price: number
  highlighted: boolean
  summary: string
}

export function usePromoPackages() {
  return useQuery({
    queryKey: ['promo-packages'],
    queryFn: () => api.get<{ packages: PromoPackage[] }>('/promo-packages'),
    // Daftar harga hampir tidak pernah berubah dalam satu kunjungan.
    staleTime: 30 * 60 * 1000,
  })
}

/** Promosi milik pemanggil, beserta hitungan tayang dan kliknya. */
export function useMyPromos() {
  return useQuery({
    queryKey: ['my-promos'],
    queryFn: () => api.get<{ promos: AdminPromo[] }>('/me/promos'),
  })
}

export interface BuyPromoInput {
  package: string
  title: string
  subtitle?: string
  image_url: string
  cta_label?: string
  target_url?: string
  accent?: PromoAccent
}

export function useBuyPromo() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: BuyPromoInput) =>
      api.post<{ promo: AdminPromo; order_id: string; pay_url: string }>('/me/promos', input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['my-promos'] })
      client.invalidateQueries({ queryKey: ['orders'] })
    },
  })
}

/** Mengunggah gambar untuk promosi yang dibeli sendiri. */
export function useUploadMyPromoImage() {
  return useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData()
      body.append('file', file)

      const response = await fetch('/api/v1/me/promos/image', {
        method: 'POST',
        credentials: 'include',
        body,
      })

      const payload = await response.json()
      if (!response.ok) {
        throw new Error(payload?.error?.message ?? 'Gambar gagal diunggah.')
      }
      return payload as { image_url: string }
    },
  })
}
